import type { App, Directive, DirectiveBinding } from 'vue'
import DOMPurify, { type Config } from 'dompurify'
import { sanitizeUrl as sanitizeUrlLibrary } from '@braintree/sanitize-url'

export type SafeHtmlDirective = Directive<HTMLElement, string | undefined>

declare module 'dompurify' {
  interface Config {
    /** All HTMLAnchorElement are render with blank target through DOMPurify "afterSanitizeAttributes" hook  */
    forceTargetBlank?: boolean
  }
}

const allowedTags = ['a', 'br', 'p']
const SanitizationLevelDefs = {
  low: {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['target'],
  },
  medium: {
    USE_PROFILES: { html: true },
    FORBID_ATTR: ['style'],
  },
  high: {
    ALLOWED_TAGS: allowedTags,
    FORBID_ATTR: ['style'],
  },
} as const satisfies Record<'low' | 'medium' | 'high', Partial<Config>>

export type SanitizationLevel = keyof typeof SanitizationLevelDefs

const handleSanitizationHook = (el: HTMLElement, { value, arg, modifiers }: DirectiveBinding) => {
  const level: SanitizationLevel = arg in SanitizationLevelDefs ? (arg as SanitizationLevel) : 'high'
  el.innerHTML = sanitizeHTML(value, level, modifiers?.blankLink).toString()
}

DOMPurify.addHook('afterSanitizeAttributes', (node, _, config) => {
  if (config.forceTargetBlank && node instanceof HTMLAnchorElement) {
    node.setAttribute('target', '_blank')
  }
})

export const sanitizeHTML = (value: string, level: SanitizationLevel = 'high', forceTargetBlank: boolean = false) => {
  const config: Config = {
    forceTargetBlank,
    ...SanitizationLevelDefs[level],
  }

  return DOMPurify.sanitize(value, config)
}

export const sanitizeUrl = (url: string): string => {
  return sanitizeUrlLibrary(url)
}

export default (app: App) => {
  app.directive('safe-html', {
    created: handleSanitizationHook,
    updated: handleSanitizationHook,
  } satisfies SafeHtmlDirective)

  app.directive('safe-link', {
    created: (el: HTMLAnchorElement, { value }) => {
      if (!(el instanceof HTMLAnchorElement)) return console.warn('v-safe-link should be used only with an A tag element <HTMLAnchorElement>')
      el.href = sanitizeUrl(value)
    },
  })
}
